import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowRight, Eye, ShoppingBag, FlaskConical, Lock } from "lucide-react";
import { meta } from "@/lib/peptideMeta";
import { activeVariants, defaultVariant, hasMultipleVariants, minVariantPrice } from "@/lib/variants";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ peptide, compact = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const m = meta(peptide.slug);
  const { user } = useAuth();
  const { addItem } = useCart();
  const canSeePricing = !!user && user.onboarding_complete;

  const variants = useMemo(() => activeVariants(peptide), [peptide]);
  const initial = defaultVariant(peptide);
  const [selectedId, setSelectedId] = useState(initial?.id || null);
  const selected = variants.find((v) => v.id === selectedId) || initial;
  const multi = hasMultipleVariants(peptide);
  const displayPrice = selected ? Number(selected.price || 0) : minVariantPrice(peptide) || Number(peptide.price || 0);

  const addToCart = () => {
    if (!selected) return;
    addItem({
      peptide_slug: peptide.slug,
      variant_id: selected.id,
      quantity: 1,
      name: peptide.name,
      variant_label: selected.label,
      unit_price: Number(selected.price || 0),
      image_url: peptide.image_url,
    });
  };

  return (
    <>
      <div
        data-testid={`product-card-${peptide.slug}`}
        className="group relative border border-slate-200 rounded-md bg-white overflow-hidden card-clinical flex flex-col"
      >
        {/* Image / molecular header */}
        <div className="relative h-44 bg-slate-50 border-b border-slate-100 overflow-hidden">
          {peptide.image_url ? (
            <img src={peptide.image_url} alt={peptide.name} className="absolute inset-0 w-full h-full object-cover"/>
          ) : (
            <>
              <div className="absolute inset-0 dot-grid opacity-70" />
              <div className="absolute inset-0 flex items-center justify-center">
                <FlaskConical className="w-14 h-14 text-blue-600/70" strokeWidth={1.25} />
              </div>
            </>
          )}
          <Badge className="absolute top-3 left-3 rounded-md bg-white/90 border border-slate-200 text-slate-700 font-mono text-[10px] uppercase tracking-widest">
            {peptide.category}
          </Badge>
          <button
            data-testid={`quick-view-${peptide.slug}`}
            onClick={() => setOpen(true)}
            className="absolute top-3 right-3 h-8 px-3 rounded-md bg-white/95 border border-slate-200 text-xs text-slate-700 hover:bg-slate-900 hover:text-white transition-colors flex items-center gap-1"
            aria-label="Quick view"
          >
            <Eye className="w-3.5 h-3.5" strokeWidth={1.75} /> Quick view
          </button>
        </div>

        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <div className="font-display text-xl font-semibold text-slate-900">{peptide.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{peptide.chemical_name}</div>
            </div>
            <div className="text-right">
              {canSeePricing ? (
                <>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    {multi ? "From" : "Price"}
                  </div>
                  <div
                    data-testid={`price-${peptide.slug}`}
                    className="font-display text-lg font-semibold text-slate-900 tabular-nums"
                  >
                    ${displayPrice.toFixed(2)}
                  </div>
                </>
              ) : (
                <button
                  onClick={() => navigate(user ? "/onboarding" : "/login")}
                  data-testid={`price-locked-${peptide.slug}`}
                  className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <Lock className="w-3 h-3" strokeWidth={2} /> {user ? "Complete profile" : "Sign in for price"}
                </button>
              )}
            </div>
          </div>

          {!compact && (
            <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{m.blurb}</p>
          )}

          {/* Variant chips */}
          {canSeePricing && multi && (
            <div
              data-testid={`variant-chips-${peptide.slug}`}
              className="mt-4 flex flex-wrap gap-1.5"
              role="radiogroup"
              aria-label={`${peptide.name} dose`}
            >
              {variants.map((v) => {
                const isSel = v.id === selected?.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    data-testid={`variant-chip-${peptide.slug}-${v.id}`}
                    onClick={(e) => { e.preventDefault(); setSelectedId(v.id); }}
                    className={`h-7 px-2.5 rounded-md text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                      isSel
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"
                    }`}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2">
            {canSeePricing ? (
              <Button
                data-testid={`add-to-cart-${peptide.slug}`}
                onClick={addToCart}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
              >
                <ShoppingBag className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Add to cart
              </Button>
            ) : (
              <Button
                data-testid={`create-account-${peptide.slug}`}
                onClick={() => navigate(user ? "/onboarding" : "/login")}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
              >
                <Lock className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> {user ? "Complete profile" : "Create account"}
              </Button>
            )}
            <Link
              to={`/peptides/${peptide.slug}`}
              data-testid={`learn-more-${peptide.slug}`}
              className="h-10 px-4 rounded-md border border-slate-300 hover:border-slate-900 text-sm text-slate-700 hover:text-slate-900 inline-flex items-center transition-colors"
            >
              Learn more
            </Link>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`quick-view-dialog-${peptide.slug}`} className="max-w-2xl rounded-md">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="rounded-md bg-blue-50 border border-blue-200 text-blue-700 font-mono text-[10px] uppercase tracking-widest">
                {peptide.category}
              </Badge>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                Research only · 21+
              </span>
            </div>
            <DialogTitle className="font-display text-3xl">{peptide.name}</DialogTitle>
            <DialogDescription className="text-slate-500">{peptide.chemical_name}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-700 leading-relaxed">{peptide.summary}</p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="border border-slate-200 rounded-md p-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Purity</div>
              <div className="mt-1 text-sm text-slate-900">{peptide.purity}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">MW</div>
              <div className="mt-1 text-sm font-mono text-slate-900">{peptide.molecular_weight}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3 col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Sequence</div>
              <div className="mt-1 text-sm font-mono text-slate-900 break-all">{peptide.sequence}</div>
            </div>
          </div>
          <div className="flex justify-between items-center pt-2">
            <div>
              {canSeePricing ? (
                <>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    {multi ? "From" : "Price"}
                  </div>
                  <div className="font-display text-2xl font-semibold text-slate-900 tabular-nums">
                    ${displayPrice.toFixed(2)}
                  </div>
                </>
              ) : (
                <div className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-blue-700">
                  <Lock className="w-3.5 h-3.5" strokeWidth={2} /> {user ? "Complete profile" : "Sign in for price"}
                </div>
              )}
            </div>
            {canSeePricing ? (
              <Link to={`/peptides/${peptide.slug}`} onClick={() => setOpen(false)}>
                <Button className="h-10 px-5 bg-slate-900 hover:bg-blue-600 text-white rounded-md transition-colors">
                  Full details <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
                </Button>
              </Link>
            ) : (
              <Button
                onClick={() => {
                  setOpen(false);
                  navigate(user ? "/onboarding" : "/login");
                }}
                className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                {user ? "Complete profile" : "Create account"} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
